import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, combineLatest, Subscription } from 'rxjs';
import { map } from 'rxjs/operators';
import { CartData, ShippingDetails, StoreLocation, WarehouseLocationCode } from 'src/app/shared-core/models/cart-data.model';
import { CheckoutData } from 'src/app/shared-core/models/checkout-data.model';
import { CartService } from 'src/app/shared-core/services/cart.service';
import { CheckoutService } from 'src/app/shared-core/services/checkout.service';
import { VistekValidation } from 'src/app/shared-core/services/validation.service';

@Component({
    selector: 'app-shipping',
    templateUrl: './shipping.component.html',
    styleUrls: ['./shipping.component.scss']
})
export class ShippingComponent implements OnInit {
    @Input() public pageScope: string;
    public postalCodeForm: FormGroup;
    public storeLocations: StoreLocation[] = [];
    public shippingDetails: ShippingDetails;
    
    public shippingRatesForm: FormGroup;
    public shippingRatesFormSubscription: Subscription;
    
    public pickupLocationsForm: FormGroup;
    public pickupLocationsFormSubscription: Subscription;
    
    public isLoading = true;
    public stockMap: Map<string, {
        allInStock: boolean,
        allOutOfStock: boolean,
        partiallyInStock: boolean
    }>;

    public get isCartPage(): boolean {
        return this.pageScope === 'cart';
    }

    public get showShippingSelector(): boolean {
        return !this.shippingDetails.IsPickup;
    }

    public get showPickupSelector(): boolean {
        return !this.shippingDetails.IsPickupDisabled &&
            this.shippingDetails.IsPickup;
    }
    
    public get shippingQuoteRequired(): boolean {
        if (!this.isCartPage) {
            return this.shippingDetails.UnableToInstantQuote;
        }
        
        return this.shippingDetails.UnableToInstantQuote && Boolean(this.shippingDetails.ShippingQuote.ShippingAddress);
    }

    constructor(
        public checkoutService: CheckoutService,
        public cartService: CartService,
        private _formBuilder: FormBuilder
    ) {
    }

    public ngOnInit(): void {
        const data$: BehaviorSubject<CartData | CheckoutData> = this.isCartPage ?
            this.cartService.ModelSubject :
            this.checkoutService.CheckoutDataSubject;

        const shippingDetails$ = data$.pipe(
            map(x => ({shippingDetails: x.ShippingDetails, cartItems: x.CartItems }))
        );

        combineLatest([
            this.checkoutService.getStores(),
            shippingDetails$
        ]).subscribe(data => {
            const [storeLocations, { shippingDetails, cartItems }] = data;

            const storesLoaded = storeLocations && storeLocations.length > 0;
            const shippingLoaded = Boolean(shippingDetails);

            if (shippingLoaded) {
                this.shippingDetails = shippingDetails;
                this.setupShippingRatesForm();
            }

            if (shippingLoaded && storesLoaded) {
                this.storeLocations = storeLocations;
                this.setupPickupLocationsForm();

                try {
                    let stock: [string, boolean][] = [];
                    
                    cartItems.forEach(item => {
                        const storeQuantities = Object.entries(item.StoreQuantities);

                        const stores: [string, boolean][] = storeQuantities
                            .map(([locationCode, storeQuantity]) => ([locationCode, storeQuantity - item.Quantity >= 0]))
    
                        stock = [ ...stock, ...stores ];
                    });

                    const groupedStock = stock.reduce((accumulator, [locationCode, isInStock]) => {
                        if (accumulator[locationCode] === undefined) {
                            accumulator[locationCode] = [];
                        }

                        accumulator[locationCode].push(isInStock);

                        return accumulator;
                    }, {});
                    
                    this.stockMap = new Map(Object.entries(groupedStock).map(([locationCode, stockStatuses]: [string, boolean[]]) => {
                        let allInStock = true;
                        let allOutOfStock = true;
                        
                        for(let stockStatus of stockStatuses) {
                            allInStock &&= stockStatus;
                            allOutOfStock &&= !stockStatus;
                        }

                        return [locationCode, {
                            allInStock: allInStock,
                            allOutOfStock: allOutOfStock,
                            partiallyInStock: !allInStock && !allOutOfStock
                        }]
                    }));
                } 
                catch {
                    this.stockMap = new Map();
                }
            }

            if (this.isCartPage) {
                this.setupPostalCodeForm();
            }

            this.isLoading = !storesLoaded || !shippingLoaded;
        });
    }

    public changeFulfillmentMethod(event: MouseEvent, method: string) {
        (event.target as HTMLElement).blur();
        
        if (
            method == 'pickup' && this.shippingDetails.IsPickupDisabled ||
            this.shippingDetails.IsPickup && method === 'pickup' || 
            !this.shippingDetails.IsPickup && method === 'shipping'
        ) {
            return;
        }
        
        if (this.isCartPage) {
            this.cartService.setShippingMethod(method);
            return;
        }

        this.checkoutService.setShippingMethod(method);
    }

    public getNewShippingQuoteFromPostalCode() {
        this.postalCodeForm.markAllAsTouched();
        this.postalCodeForm.updateValueAndValidity();

        if (!this.postalCodeForm.valid) {
            return;
        }

        const postalCode = this.postalCodeForm.get('postalCode').value;
        this.cartService.getShippingRatesFromPostalCode(postalCode);
    }

    private setupShippingRatesForm(): void {
        if (this.shippingRatesFormSubscription) {
            this.shippingRatesFormSubscription.unsubscribe();
        }

        this.shippingRatesForm = this._formBuilder.group({
            selectedConsolidatedShippingRateId: [this.shippingDetails.SelectedConsolidatedShippingRateId, Validators.required],
            requestInsurance: [this.shippingDetails.RequestInsurance && this.shippingDetails.ShippingQuote.IsEligibleForInsurance]
        });

        this.shippingRatesFormSubscription = this.shippingRatesForm.valueChanges.subscribe(x => {
            // Logic: To ensure that the checkbox is unchecked when not eligible for insurance,
            // and to ensure that we preserve the users selection when it becomes eligible again,
            // We re-use the saved requestInsurance value if the shipping quote is not eligible for insurance.
            // If the shipping quote is eligible for insurance, we use the value from the form.
            let requestInsurance = true;
            if (!x.requestInsurance) {
                if (!this.shippingDetails.ShippingQuote.IsEligibleForInsurance) {
                    requestInsurance = this.shippingDetails.RequestInsurance;
                }
                else {
                    requestInsurance = false;
                }
            }
            
            
            const rate = this.shippingDetails
                .ShippingQuote
                .ConsolidatedShippingRates.find(y => y.ConsolidatedShippingRateId === x.selectedConsolidatedShippingRateId);            

            if (this.isCartPage) {
                this.cartService.selectConsolidatedShippingRate(rate.ShippingCompanyId, rate.ShippingServiceId, requestInsurance);
            }
            else {
                this.checkoutService.loadNewShippingRateSelection(rate.ShippingCompanyId, rate.ShippingServiceId, requestInsurance);
            }
        });
    }

    private setupPickupLocationsForm(): void {
        if (this.pickupLocationsFormSubscription) {
            this.pickupLocationsFormSubscription.unsubscribe();
        }

        const pickupLocation = this.shippingDetails?.PickupLocationCode ?? WarehouseLocationCode.Toronto;

        this.pickupLocationsForm = this._formBuilder.group({
            pickupLocation: [pickupLocation, Validators.required]
        });

        this.pickupLocationsFormSubscription = this.pickupLocationsForm.get('pickupLocation').valueChanges.subscribe(x => {
            if (this.isCartPage) {
                this.cartService.selectPickupLocation(x);
            }
            else {
                this.checkoutService.selectPickupLocation(x);
            }
        });
    }

    private setupPostalCodeForm(): void {
        const address = this.shippingDetails?.ShippingQuote?.ShippingAddress;

        const postalCode = (address?.PostalCode ?? '').replace(/ /g, '');

        this.postalCodeForm = this._formBuilder.group({
            postalCode: [ postalCode, [ Validators.required, VistekValidation.postalCodeZip(null) ] ]
        });
    }
}